import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Plus, UserMinus, Loader2 } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { follow, unfollow } from "@/lib/follows";

/**
 * Unilateral follow control. Not following → "Follow". Following → "Following",
 * and a second press asks once for confirmation before unfollowing.
 */
export function FollowButton({
  targetId,
  initialFollowing,
  size = "default",
  className,
  onChange,
}: {
  targetId: string;
  initialFollowing: boolean;
  size?: "default" | "sm";
  className?: string;
  onChange?: (following: boolean) => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [following, setFollowing] = useState(initialFollowing);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const lock = useRef(false);


  useEffect(() => setFollowing(initialFollowing), [initialFollowing]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  if (user?.id === targetId) return null;

  const press = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy || lock.current) return;
    if (!user) {
      navigate({ to: "/signin", search: { redirect: window.location.pathname } as never });
      return;
    }
    if (following && !confirming) {
      setConfirming(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setConfirming(false), 3000);
      return;
    }
    lock.current = true;
    setBusy(true);
    window.clearTimeout(timer.current);
    const next = !following;
    setFollowing(next);
    setConfirming(false);
    try {
      if (next) await follow(user.id, targetId);
      else await unfollow(user.id, targetId);
      onChange?.(next);
    } catch {
      setFollowing(!next);
      toast.error("That didn't go through. Try again.");
    } finally {
      setBusy(false);
      lock.current = false;
    }
  };

  const label = busy
    ? following
      ? "Following…"
      : "Follow…"
    : confirming
      ? "Unfollow?"
      : following
        ? "Following"
        : "Follow";
  const Icon = confirming ? UserMinus : following ? Check : Plus;

  return (
    <motion.button
      type="button"
      onClick={press}
      disabled={busy}
      aria-busy={busy}
      aria-pressed={following}
      aria-label={confirming ? "Confirm unfollow" : following ? "Unfollow" : "Follow"}
      whileTap={busy ? undefined : { scale: 0.97 }}
      transition={{ duration: 0.16, ease: [0.22, 0.61, 0.36, 1] }}
      className={cn(
        "inline-flex min-h-11 touch-manipulation select-none items-center justify-center gap-1.5 rounded-full border font-semibold transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-60",
        size === "sm" ? "min-h-11 px-4 text-[13px] sm:min-h-9" : "px-5 text-sm",
        confirming
          ? "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15"
          : following
            ? "border-border bg-transparent text-foreground hover:bg-muted"
            : "border-foreground bg-foreground text-background hover:opacity-90",
        className,
      )}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}

      <motion.span
        key={label ?? "busy"}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
      >
        {label}
      </motion.span>
    </motion.button>
  );
}
